/* eslint-disable @typescript-eslint/no-explicit-any */
import type { AxiosRequestConfig, CancelToken } from "axios";

import { LANG_DEFAULT, LANG_HEADER_NAME } from "../../constants";
import type {
  RequestBodyModel,
  RequestHeadersModel,
  TApiSuccess,
} from "../../models";
import { doApiCall } from "../api-utils";
import { apiBaseUrl } from "../env.helpers";
import { Log } from "../log.helpers";
import { getLangLocalStorage } from "../rtl.helpers";
import { normalizeUrlBasePath } from "../url.helpers";

export class Api {
  static get baseUrl(): string | undefined {
    return normalizeUrlBasePath(apiBaseUrl() || "");
  }

  private static getRequestHeaders(
    headers?: RequestHeadersModel,
  ): Record<string, any> {
    return {
      Accept: "application/json",
      [LANG_HEADER_NAME]: getLangLocalStorage() || LANG_DEFAULT,
      ...headers,
    };
  }

  static async get<T>(
    url: string,
    body?: RequestBodyModel,
    controller?: AbortController,
    headers?: RequestHeadersModel,
    _authorize = true,
    cancelToken?: CancelToken,
  ): Promise<TApiSuccess<T> | undefined> {
    let query = "";

    if (body) {
      query = "?" + new URLSearchParams(body).toString();
      Log.debug("Get request with query", query);
    }

    const requestHeaders = this.getRequestHeaders(headers);

    const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
      baseURL: this.baseUrl,
      url: url + query,
      method: "GET",
      headers: requestHeaders,
      signal: controller?.signal,
      cancelToken,
    };

    return doApiCall(axiosConfig);
  }

  static async post<T>(
    url: string,
    body?: RequestBodyModel,
    controller?: AbortController,
    headers?: RequestHeadersModel,
    _authorize = true,
    cancelToken?: CancelToken,
    responseType?: "json" | "text" | "blob",
  ): Promise<TApiSuccess<T> | undefined> {
    const requestHeaders = this.getRequestHeaders(headers);

    const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
      baseURL: this.baseUrl,
      url: url,
      data: body,
      method: "POST",
      headers: requestHeaders,
      signal: controller?.signal,
      cancelToken,
    };

    if (responseType) {
      axiosConfig.responseType = responseType;
    }

    return doApiCall(axiosConfig);
  }

  static async postExtended<T>({
    url,
    body,
    abortController,
    headers,
    authorize: _authorize = true,
    cancelToken,
    responseType,
  }: {
    url: string;
    body?: RequestBodyModel;
    abortController?: AbortController;
    headers?: RequestHeadersModel;
    authorize?: boolean;
    cancelToken?: CancelToken;
    responseType?: "json" | "text" | "blob";
  }): Promise<TApiSuccess<T> | undefined> {
    const requestHeaders = this.getRequestHeaders(headers);

    const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
      baseURL: this.baseUrl,
      url: url,
      data: body,
      method: "POST",
      headers: requestHeaders,
      signal: abortController?.signal,
      cancelToken,
    };

    if (responseType) {
      axiosConfig.responseType = responseType;
    }

    return doApiCall(axiosConfig);
  }

  static async patch<T>(
    url: string,
    body?: RequestBodyModel,
    controller?: AbortController,
    headers?: RequestHeadersModel,
    _authorize = true,
    cancelToken?: CancelToken,
  ): Promise<TApiSuccess<T> | undefined> {
    const requestHeaders = this.getRequestHeaders(headers);

    const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
      baseURL: this.baseUrl,
      url: url,
      data: body,
      method: "PATCH",
      headers: requestHeaders,
      signal: controller?.signal,
      cancelToken,
    };

    return doApiCall(axiosConfig);
  }

  static async delete<T>(
    url: string,
    body?: RequestBodyModel,
    controller?: AbortController,
    headers?: RequestHeadersModel,
    _authorize = true,
    cancelToken?: CancelToken,
  ): Promise<TApiSuccess<T> | undefined> {
    const requestHeaders = this.getRequestHeaders(headers);

    const axiosConfig: AxiosRequestConfig<RequestBodyModel> = {
      baseURL: this.baseUrl,
      url: url,
      data: body,
      method: "DELETE",
      headers: requestHeaders,
      signal: controller?.signal,
      cancelToken,
    };

    return doApiCall(axiosConfig);
  }
}
